import {
  AddOutlined,
  AssignmentOutlined,
  EditOutlined,
  LockOpenOutlined,
  LockOutlined,
  SearchOutlined
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AllocationSimulation,
  StockAllocation,
  createStockAllocation,
  loadApplicationSettings,
  loadStockAllocations,
  loadStockByItem,
  loadStockByWarehouse,
  loadStockUnits,
  releaseStockAllocation,
  simulateStockAllocation
} from "./api";
import { ListRowActions } from "./ListRowActions";
import { operationalStatusColor, operationalStatusLabel } from "./operationalStatus";
import { matchesListSyntax } from "./listSearch";
import { PageHeader } from "./PageHeader";
import { LabeledInfo } from "./LabeledInfo";
import { MobileActionBar } from "./MobileActionBar";
import { MobileOperationalHeader } from "./MobileOperationalHeader";
import { ResponsiveStatusTabs } from "./ResponsiveStatusTabs";

function q(value: number, locale: string) {
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 6
  }).format(value);
}

export function AllocationPage({
  onBack,
  onPrepare,
  permissions,
  initialTargetId = null,
  initialOpenDetail = false,
  initialSearch = ""
}: {
  onBack: () => void;
  onPrepare: (allocationId: string) => void;
  permissions: string[];
  initialTargetId?: string | null;
  initialOpenDetail?: boolean;
  initialSearch?: string;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === "en" ? "en-US" : "fr-FR";
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<"open" | "closed">("open");
  const [search, setSearch] = useState(initialSearch);
  useEffect(() => setSearch(initialSearch), [initialSearch]);
  const [createOpen, setCreateOpen] = useState(false);
  const [itemKey, setItemKey] = useState("");
  const [warehouseKey, setWarehouseKey] = useState("");
  const [requestedQuantity, setRequestedQuantity] = useState("1");
  const [reference, setReference] = useState("");
  const [simulation, setSimulation] =
    useState<AllocationSimulation | null>(null);
  const [toRelease, setToRelease] = useState<StockAllocation | null>(null);
  const [releaseReason, setReleaseReason] = useState("");
  const [mobileSelection, setMobileSelection] =
    useState<StockAllocation | null>(null);

  const allocations = useQuery({
    queryKey: ["stock-allocations"],
    queryFn: loadStockAllocations
  });
  useEffect(() => {
    if (!initialTargetId || !allocations.data) return;
    const target = allocations.data.find((entry) => entry.id === initialTargetId);
    if (!target) return;
    setTab(["OPEN", "PICKING"].includes(target.status) ? "open" : "closed");
    if (initialOpenDetail && target.status === "OPEN") setToRelease(target);
    window.setTimeout(() => document.getElementById(
      `allocation-${initialTargetId}`
    )?.scrollIntoView({ behavior: "smooth", block: "center" }), 0);
  }, [allocations.data, initialOpenDetail, initialTargetId]);
  const applicationSettings = useQuery({
    queryKey: ["application-settings"],
    queryFn: loadApplicationSettings
  });
  const reservationMinutes = applicationSettings.data?.find((setting) =>
    setting.key === "ALLOCATION_DEFAULT_RESERVATION_MINUTES"
  )?.value ?? "30";
  const items = useQuery({
    queryKey: ["stock-by-item"],
    queryFn: loadStockByItem
  });
  const warehouses = useQuery({
    queryKey: ["stock-by-warehouse"],
    queryFn: loadStockByWarehouse
  });
  const selectedItem = items.data?.find((entry) =>
    `${entry.itemId}|${entry.uom}` === itemKey);
  const itemUnits = useQuery({
    queryKey: ["allocation-item-units", selectedItem?.itemId],
    queryFn: () => loadStockUnits(selectedItem!.itemId),
    enabled: Boolean(selectedItem)
  });
  const selectedWarehouse = warehouses.data?.find((entry) =>
    `${entry.warehouseId}|${entry.clientId ?? ""}|${entry.uom}`
      === warehouseKey);
  function itemAvailability(
    warehouseId: string,
    clientId: string | null,
    uom: string
  ) {
    const onHand = (itemUnits.data ?? []).filter((unit) =>
      unit.warehouseId === warehouseId
      && unit.clientId === clientId
      && unit.uom === uom
      && unit.qualityStatus === "AVAILABLE"
      && unit.stockStatus === "ON_HAND"
      && unit.handlingUnitStatus === "CLOSED"
    ).reduce((total, unit) => total + Number(unit.quantity), 0);
    const reserved = (allocations.data ?? []).filter((allocation) =>
      ["OPEN", "PICKING", "PICKED"].includes(allocation.status)
      && allocation.itemId === selectedItem?.itemId
      && allocation.warehouseId === warehouseId
      && allocation.clientId === clientId
      && allocation.uom === uom
    ).flatMap((allocation) => allocation.lines)
      .reduce((total, line) => total + Number(line.allocatedQuantity), 0);
    return Math.max(0, onHand - reserved);
  }
  const request = selectedItem && selectedWarehouse ? {
    itemId: selectedItem.itemId,
    warehouseId: selectedWarehouse.warehouseId,
    clientId: selectedWarehouse.clientId,
    quantity: Number(requestedQuantity),
    uom: selectedItem.uom,
    requestReference: reference.trim() || null,
    expirationMinutes: Number(reservationMinutes)
  } : null;
  function resetCreateForm() {
    setItemKey("");
    setWarehouseKey("");
    setRequestedQuantity("1");
    setReference("");
    setSimulation(null);
    simulate.reset();
    create.reset();
  }
  const simulate = useMutation({
    mutationFn: () => simulateStockAllocation(request!),
    onSuccess: setSimulation
  });
  const create = useMutation({
    mutationFn: () => createStockAllocation(request!),
    onSuccess: async () => {
      setCreateOpen(false);
      resetCreateForm();
      await queryClient.invalidateQueries({ queryKey: ["stock-allocations"] });
    }
  });
  const release = useMutation({
    mutationFn: () => releaseStockAllocation(
      toRelease!.id, releaseReason.trim(), toRelease!.version
    ),
    onSuccess: async () => {
      setToRelease(null);
      setReleaseReason("");
      await queryClient.invalidateQueries({ queryKey: ["stock-allocations"] });
    }
  });
  const filtered = useMemo(() => (allocations.data ?? []).filter((entry) =>
    (tab === "open" ? ["OPEN", "PICKING"].includes(entry.status)
      : !["OPEN", "PICKING"].includes(entry.status))
    && (!search.trim() || matchesListSyntax({
      id: `${entry.id} ${entry.requestReference}`,
      article: `${entry.itemCode} ${entry.itemName}`,
      item: `${entry.itemCode} ${entry.itemName}`,
      entrepot: entry.warehouseCode,
      warehouse: entry.warehouseCode,
      client: entry.clientCode ?? "Stock propre Own stock",
      strategie: entry.rotationPolicy,
      strategy: entry.rotationPolicy,
      statut: entry.status,
      status: entry.status,
      hu: entry.lines.map((line) => line.handlingUnitNumber).join(" "),
      lot: entry.lines.map((line) => line.lotNumber ?? "").join(" "),
      date: `${entry.createdAt} ${entry.expiresAt}`
    }, search))
  ), [allocations.data, search, tab]);
  const canCreate = permissions.includes("allocation:create");
  const canRelease = permissions.includes("allocation:release");
  const canPrepare = permissions.includes("picking:create");
  const openCount = (allocations.data ?? []).filter((entry) =>
    ["OPEN", "PICKING"].includes(entry.status)).length;
  const closedCount = (allocations.data?.length ?? 0) - openCount;
  const openCreateDialog = () => {
    resetCreateForm();
    setCreateOpen(true);
  };
  const simulationCheckLabel = (check: string) => {
    const shelfLife = check.match(/^MINIMUM_REMAINING_SHELF_LIFE_(\d+)_DAYS$/);
    if (shelfLife) return t("allocation.labels.minimumShelfLife", {
      days: shelfLife[1]
    });
    if (check.startsWith("POLICY_")) return check.slice(7).replaceAll("_", " ");
    return t(`allocation.simulationChecks.${check}`, {
      defaultValue: check.replaceAll("_", " ")
    });
  };

  return (
    <Stack spacing={1.5} sx={{
      height: { xs: "100%", sm: "auto", md: "100%" }, minHeight: 0,
      overflow: { xs: "hidden", sm: "visible", md: "hidden" }
    }}>
      <MobileOperationalHeader moduleLabel={t("allocation.module")}
        search={search} onSearchChange={setSearch}
        searchPlaceholder={t("allocation.searchPlaceholder")} onBack={onBack} />
      <Box sx={{ display: { xs: "none", sm: "block" }, flexShrink: 0 }}>
        <PageHeader title={t("allocation.title")} compact
          onBack={onBack}
          actions={canCreate ? (
            <Button variant="contained" startIcon={<AddOutlined />}
              data-testid="allocation-new" onClick={openCreateDialog}>
              {t("allocation.actions.new")}
            </Button>
          ) : undefined} />
      </Box>
      <ResponsiveStatusTabs value={tab} items={[
        { value: "open", label: t("allocation.tabs.open"),
          mobileLabel: t("allocation.tabsMobile.open"),
          count: openCount },
        { value: "closed", label: t("allocation.tabs.closed"),
          mobileLabel: t("allocation.tabsMobile.closed"),
          count: closedCount }
      ]} onChange={(value) => {
        setTab(value);
        setMobileSelection(null);
      }} />
      <Card variant="outlined" sx={{ display: { xs: "none", sm: "block" },
        flexShrink: 0 }}>
          <CardContent sx={{ py: 1.25, "&:last-child": { pb: 1.25 } }}>
            <TextField fullWidth size="small" label={t("common.filters.search")} value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("allocation.searchPlaceholder")}
              InputProps={{ startAdornment:
                <SearchOutlined color="action" sx={{ mr: 1 }} /> }} />
          </CardContent>
      </Card>
      <Box sx={{ minHeight: 0, flex: { xs: "1 1 0", sm: "none", md: 1 },
        overflowY: { xs: "auto", sm: "visible", md: "auto" },
        pr: .5, pb: { xs: 11, md: 0 } }}>
      <Stack spacing={1.5}>
        {allocations.isError && (
          <Alert severity="error">{t("allocation.errors.load")}</Alert>
        )}
        {!allocations.isLoading && filtered.length === 0 && (
          <Alert severity="info">{t("allocation.empty.list")}</Alert>
        )}
        {filtered.map((entry) => (
          <Card key={entry.id} id={`allocation-${entry.id}`} variant="outlined"
            onClick={(event) => {
              if ((event.target as HTMLElement).closest("button")) return;
              setMobileSelection(entry);
            }}
            sx={{ cursor: { xs: "pointer", sm: "default" },
              outline: { xs: mobileSelection?.id === entry.id
                ? "2px solid" : "none", sm: "none" },
              outlineColor: { xs: "primary.main", sm: "transparent" },
              borderColor: entry.id === initialTargetId
              ? "primary.main" : undefined, borderWidth: entry.id === initialTargetId
                ? 2 : undefined }}>
            <CardContent>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}
                alignItems={{ md: "center" }}>
                <LockOutlined color="secondary" />
                <Stack spacing={1.5} sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}>
                    <LabeledInfo label={t("allocation.labels.requestReference")} prominent>{entry.requestReference}</LabeledInfo>
                    <LabeledInfo label={t("allocation.card.warehouse")}>{entry.warehouseCode}</LabeledInfo>
                    <LabeledInfo label={t("allocation.card.client")}>{entry.clientCode ?? t("allocation.labels.ownStock")}</LabeledInfo>
                  </Stack>
                  <Box sx={{ p: 1.5, bgcolor: "background.default", borderRadius: 1 }}>
                    <Stack spacing={1.5}>
                      <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}>
                        <LabeledInfo label={t("allocation.card.item")} prominent>{entry.itemCode}</LabeledInfo>
                        <LabeledInfo label={t("allocation.card.description")} prominent>{entry.itemName}</LabeledInfo>
                        <LabeledInfo label={t("allocation.card.requested")} prominent>{q(entry.requestedQuantity, locale)} {entry.uom}</LabeledInfo>
                        <LabeledInfo label={t("allocation.card.huCount")}>{entry.lines.length}</LabeledInfo>
                        <LabeledInfo label={t("allocation.card.rotation")}>{entry.rotationPolicy}</LabeledInfo>
                      </Stack>
                      {entry.lines.map((line) => (
                        <Stack key={line.inventoryUnitId} direction="row" useFlexGap flexWrap="wrap"
                          columnGap={3} rowGap={1} sx={{ pt: 1, borderTop: 1, borderColor: "divider" }}>
                          <LabeledInfo label={t("allocation.card.sequence")}>{line.sequenceNumber}</LabeledInfo>
                          <LabeledInfo label={t("allocation.card.hu")} prominent>{line.handlingUnitNumber}</LabeledInfo>
                          <LabeledInfo label={t("allocation.card.allocated")} prominent>{q(line.allocatedQuantity, locale)} {line.uom}</LabeledInfo>
                          <LabeledInfo label={t("allocation.card.location")} prominent>{line.locationCode}</LabeledInfo>
                          {line.lotNumber && <LabeledInfo label={t("allocation.card.lot")}>{line.lotNumber}</LabeledInfo>}
                          {line.expirationDate && <LabeledInfo label={t("allocation.card.expiration")}>{new Date(line.expirationDate).toLocaleDateString(locale)}</LabeledInfo>}
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                  <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}>
                    <LabeledInfo label={t("allocation.card.created")}>{new Date(entry.createdAt).toLocaleString(locale)}</LabeledInfo>
                    <LabeledInfo label={t("allocation.card.expires")}>{new Date(entry.expiresAt).toLocaleString(locale)}</LabeledInfo>
                    <LabeledInfo label={t("allocation.card.createdBy")}>{entry.createdByDisplayName ?? t("allocation.labels.unknownUser")}</LabeledInfo>
                    {entry.releaseReason && <LabeledInfo label={t("allocation.labels.reasonField")}>{entry.releaseReason}</LabeledInfo>}
                  </Stack>
                </Stack>
                <ListRowActions actions={
                  entry.status === "OPEN" ? (
                    <Stack direction="row" spacing={1}>
                      {canPrepare && (
                        <Button variant="contained"
                          startIcon={<AssignmentOutlined />}
                          data-testid={`allocation-prepare-${entry.id}`}
                          onClick={() => onPrepare(entry.id)}>
                          {t("allocation.actions.prepare")}
                        </Button>
                      )}
                      {canRelease && (
                        <Button variant="outlined" color="error"
                          onClick={() => {
                            setToRelease(entry);
                            setReleaseReason("");
                          }}>
                          {t("allocation.actions.release")}
                        </Button>
                      )}
                    </Stack>
                  ) : undefined
                } status={
                  <Chip label={operationalStatusLabel(entry.status)}
                    color={operationalStatusColor(entry.status)} />
                } />
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
      </Box>

      <MobileActionBar
        secondaryIcon={canCreate ? <AddOutlined /> : undefined}
        secondaryLabel={t("allocation.actions.new")}
        secondaryDisplayLabel={t("allocation.actions.newShort")}
        onSecondary={canCreate ? openCreateDialog : undefined}
        additionalIcon={canRelease ? <LockOpenOutlined /> : undefined}
        additionalLabel={t("allocation.actions.releaseAllocation")}
        additionalDisplayLabel={t("allocation.actions.release")}
        additionalDisabled={!mobileSelection || mobileSelection.status !== "OPEN"}
        onAdditional={canRelease && mobileSelection?.status === "OPEN" ? () => {
          setToRelease(mobileSelection);
          setReleaseReason("");
        } : undefined}
        primaryLabel={!mobileSelection ? t("allocation.actions.select")
          : mobileSelection.status === "OPEN" ? t("allocation.actions.prepare")
            : t("allocation.actions.closed")}
        primaryDisabled={!mobileSelection || mobileSelection.status !== "OPEN"
          || !canPrepare}
        onPrimary={mobileSelection?.status === "OPEN" && canPrepare
          ? () => onPrepare(mobileSelection.id) : undefined} />

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)}
        fullWidth maxWidth="md">
        <DialogTitle>{t("allocation.labels.newDialog")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField select required label={t("allocation.labels.itemUom")}
              data-testid="allocation-item"
              disabled={Boolean(simulation)}
              value={itemKey}
              onChange={(event) => {
                setItemKey(event.target.value);
                setSimulation(null);
              }}>
              {(items.data ?? []).map((entry) => (
                <MenuItem key={`${entry.itemId}-${entry.uom}`}
                  value={`${entry.itemId}|${entry.uom}`}>
                  {entry.itemCode} · {entry.itemName} · {entry.uom}
                </MenuItem>
              ))}
            </TextField>
            <TextField select required label={t("allocation.labels.warehouseOwner")}
              data-testid="allocation-warehouse-owner"
              disabled={Boolean(simulation)}
              value={warehouseKey}
              onChange={(event) => {
                setWarehouseKey(event.target.value);
                setSimulation(null);
              }}>
              {(warehouses.data ?? [])
                .filter((entry) => !selectedItem
                  || entry.uom === selectedItem.uom)
                .map((entry) => (
                  <MenuItem
                    key={`${entry.warehouseId}-${entry.clientId}-${entry.uom}`}
                    value={`${entry.warehouseId}|${entry.clientId ?? ""}|${entry.uom}`}>
                    {entry.warehouseCode} · {entry.clientCode
                      ?? t("allocation.labels.ownStock")}
                    {selectedItem
                      ? ` · ${t("allocation.labels.itemAvailable", {
                        quantity: q(itemAvailability(
                          entry.warehouseId, entry.clientId, entry.uom
                        ), locale), uom: entry.uom
                      })}`
                      : ""}
                  </MenuItem>
                ))}
            </TextField>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField type="number" required label={t("allocation.labels.quantity")}
                data-testid="allocation-quantity"
                disabled={Boolean(simulation)}
                value={requestedQuantity}
                onChange={(event) => {
                  setRequestedQuantity(event.target.value);
                  setSimulation(null);
                }} inputProps={{ min: 0.000001, step: "any" }}
                sx={{ flex: 1 }} />
              <TextField label={t("allocation.labels.reservationDuration")}
                value={reservationMinutes}
                disabled
                helperText={t("allocation.help.reservationReadonly")}
                sx={{ flex: 1 }} />
            </Stack>
            <TextField label={t("allocation.labels.requestReference")} value={reference}
              data-testid="allocation-reference"
              disabled={Boolean(simulation)}
              onChange={(event) => setReference(event.target.value)}
              helperText={t("allocation.help.optionalReference")} />
            {simulate.isError && (
              <Alert severity="error">
                {simulate.error instanceof Error
                  ? simulate.error.message : t("allocation.errors.simulation")}
              </Alert>
            )}
            {simulation && (
              <Alert severity={simulation.complete ? "success" : "warning"}>
                <Typography fontWeight={800}>
                  {simulation.complete
                    ? t("allocation.labels.complete")
                    : t("allocation.labels.insufficient")}
                  {" · "}{simulation.rotationPolicy}
                </Typography>
                <Typography variant="body2">
                  {q(simulation.allocatedQuantity, locale)} /{" "}
                  {q(simulation.requestedQuantity, locale)} {simulation.uom}
                  {" · "}{simulation.checks.map(simulationCheckLabel).join(" · ")}
                </Typography>
                {simulation.lines.map((line) => (
                  <Typography key={line.inventoryUnitId}
                    variant="caption" display="block">
                    {line.sequenceNumber}. {line.handlingUnitNumber}
                    {" · "}{q(line.allocatedQuantity, locale)} {line.uom}
                    {" · "}{line.locationCode}
                    {line.lotNumber ? ` · ${t("allocation.labels.lot", {
                      lot: line.lotNumber
                    })}` : ""}
                    {line.expirationDate
                      ? ` · ${t("allocation.labels.expiration", {
                        date: new Date(line.expirationDate).toLocaleDateString(locale)
                      })}` : ""}
                  </Typography>
                ))}
                {simulation.exclusions.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" fontWeight={800}>
                      {t("allocation.labels.excludedStock")}
                    </Typography>
                    {simulation.exclusions.map((exclusion) => (
                      <Typography key={exclusion.inventoryUnitId}
                        variant="caption" display="block">
                        {exclusion.handlingUnitNumber}
                        {exclusion.lotNumber
                          ? ` · ${t("allocation.labels.lot", {
                            lot: exclusion.lotNumber
                          })}` : ""}
                        {" · "}{t("allocation.labels.expiration", {
                          date: new Date(exclusion.expirationDate)
                            .toLocaleDateString(locale)
                        })}
                        {" · "}
                        {t(`allocation.exclusions.${exclusion.reason}`, {
                          days: simulation.minimumRemainingShelfLifeDays ?? 0
                        })}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Alert>
            )}
            {create.isError && (
              <Alert severity="error">
                {create.error instanceof Error
                  ? create.error.message : t("allocation.errors.reserve")}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>
            {t("common.actions.cancel")}
          </Button>
          <Button variant="outlined" startIcon={<EditOutlined />}
            disabled={!simulation}
            onClick={() => {
              setSimulation(null);
              simulate.reset();
              create.reset();
            }}>
            {t("allocation.actions.correct")}
          </Button>
          <Button variant="outlined" disabled={!request
            || request.quantity <= 0 || simulate.isPending
            || Boolean(simulation)}
            data-testid="allocation-simulate"
            onClick={() => simulate.mutate()}>
            {t("allocation.actions.simulate")}
          </Button>
          <Button variant="contained"
            disabled={!simulation?.complete || create.isPending}
            data-testid="allocation-reserve"
            onClick={() => create.mutate()}>
            {t("allocation.actions.reserve")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(toRelease)}
        onClose={() => setToRelease(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t("allocation.labels.releaseDialog", {
          reference: toRelease?.requestReference ?? ""
        })}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="warning">
              {t("allocation.help.releaseWarning")}
            </Alert>
            <TextField required multiline minRows={3}
              label={t("allocation.labels.reasonField")}
              value={releaseReason}
              onChange={(event) => setReleaseReason(event.target.value)}
              helperText={t("allocation.help.requiredThree")} />
            {release.isError && (
              <Alert severity="error">
                {release.error instanceof Error
                  ? release.error.message : t("allocation.errors.release")}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToRelease(null)}>
            {t("common.actions.cancel")}
          </Button>
          <Button variant="contained" color="error"
            disabled={releaseReason.trim().length < 3 || release.isPending}
            onClick={() => release.mutate()}>
            {t("allocation.actions.releaseReservation")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
